import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Award, LogOut, ExternalLink } from "lucide-react";
import CertificateCard from "@/components/CertificateCard";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";

export default function Profile() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    api.get("/profile").then((r) => setData(r.data)).catch(() => {});
    api.get("/certificates/evaluate").then((r) => setCerts(r.data.certificates || [])).catch(() => {});
  }, []);
  if (!data) return <div className="text-[var(--bone-faint)] text-sm">Loading profile…</div>;

  const radarData = Object.entries(data.stats || {}).map(([k, v]) => ({ stat: (data.stat_labels?.[k] || k).split(" ")[0], value: v }));
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-display font-semibold">Profile</h1>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="litra-card p-5">
          <div className="flex items-center gap-3">
            <Avatar className="w-14 h-14"><AvatarFallback className="bg-[rgba(20,90,67,0.3)] text-[var(--bone)] font-display">{(data.user?.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
            <div>
              <div className="font-display font-semibold text-lg">{data.user?.username}</div>
              <div className="text-xs text-[var(--bone-faint)]">{data.user?.email}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="green-text border-[rgba(20,90,67,0.5)]" data-testid="profile-rank-label">{data.progress?.rank}</Badge>
            <Badge className="gold-edge gold-text bg-transparent">Level {data.progress?.level}</Badge>
            <Badge variant="outline" data-testid="profile-plan-label">{data.plan?.name}</Badge>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-white/[0.03] p-2"><div className="text-lg font-display">{data.counts?.decode_count}</div><div className="mono-chip text-[var(--bone-faint)]">decodes</div></div>
            <div className="rounded-lg bg-white/[0.03] p-2"><div className="text-lg font-display">{data.counts?.lessons_done}</div><div className="mono-chip text-[var(--bone-faint)]">lessons</div></div>
            <div className="rounded-lg bg-white/[0.03] p-2"><div className="text-lg font-display">{data.counts?.rooms_unlocked}</div><div className="mono-chip text-[var(--bone-faint)]">rooms</div></div>
          </div>
          <Button variant="outline" className="w-full mt-4 glass" onClick={() => navigate(`/u/${encodeURIComponent(data.user?.username || "")}`)} data-testid="profile-view-public-button"><ExternalLink className="w-4 h-4 mr-2" />View public profile</Button>
          <Button variant="outline" className="w-full mt-2 glass" onClick={doLogout} data-testid="profile-logout-button"><LogOut className="w-4 h-4 mr-2" />Log out</Button>
        </div>

        <div className="litra-card p-4">
          <div className="text-sm font-display font-semibold mb-1">Player Stats</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="rgba(185,192,204,0.15)" />
                <PolarAngleAxis dataKey="stat" tick={{ fill: "#A9A39A", fontSize: 9 }} />
                <Radar dataKey="value" stroke="#2E9E76" fill="#145A43" fillOpacity={0.45} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="litra-card p-5">
          <div className="text-sm font-display font-semibold mb-3">Letter Cards · {data.letter_cards?.length}/26</div>
          <div className="flex flex-wrap gap-1.5">
            {data.letter_cards?.length ? data.letter_cards.map((l) => (
              <span key={l} className="w-8 h-8 rounded-lg flex items-center justify-center font-display green-text bg-[rgba(20,90,67,0.15)] border border-[rgba(20,90,67,0.4)]">{l}</span>
            )) : <div className="text-xs text-[var(--bone-faint)]">Complete letter lessons to unlock cards.</div>}
          </div>
          <div className="mt-5 text-sm font-display font-semibold mb-2">Certificates · {certs.length}</div>
          {certs.length ? certs.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-sm gold-text"><Award className="w-4 h-4" />{c.title}</div>
          )) : <div className="text-xs text-[var(--bone-faint)]">No certificates yet.</div>}
        </div>
      </div>

      {certs.length > 0 && (
        <div>
          <div className="text-sm font-display font-semibold flex items-center gap-2 mb-3"><Award className="w-4 h-4 gold-text" />Your Certificates &amp; Badges</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-certificates-grid">
            {certs.map((c) => <CertificateCard key={c.id} certificate={c} username={data.user?.username} />)}
          </div>
        </div>
      )}
    </div>
  );
}
